import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { ensureUserProfile, getUserProfile, watchAssumedOrgSignal } from '@/lib/firestore';
import type { Role, UserProfile } from '@/types/models';

interface Claims {
  role?: Role;
  orgId?: string | null;
  assumedOrgId?: string | null;
}

interface AuthCtx {
  user: User | null;
  profile: UserProfile | null;
  claims: Claims;
  loading: boolean;
  /** Effective orgId — uses adminAssumeOrg override when set */
  effectiveOrgId: string | null;
  isGlobalAdmin: boolean;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser]       = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [claims, setClaims]   = useState<Claims>({});
  const [loading, setLoading] = useState(true);

  // Latest claims kept in a ref so the assume-signal listener can compare
  // against the freshest value without re-subscribing on every claim change.
  const claimsRef = useRef<Claims>({});
  claimsRef.current = claims;

  const hydrate = async (u: User | null) => {
    if (!u) {
      setUser(null); setProfile(null); setClaims({}); setLoading(false);
      return;
    }
    await ensureUserProfile(u.uid, u.email ?? '', u.displayName);
    const tok = await u.getIdTokenResult();
    setClaims({
      role:         tok.claims.role as Role | undefined,
      orgId:        (tok.claims.orgId as string | null | undefined) ?? null,
      assumedOrgId: (tok.claims.assumedOrgId as string | null | undefined) ?? null,
    });
    setProfile(await getUserProfile(u.uid));
    setUser(u);
    setLoading(false);
  };

  useEffect(() => onAuthStateChanged(auth, hydrate), []);

  const refresh = async () => {
    if (auth.currentUser) {
      await auth.currentUser.getIdToken(true);
      await hydrate(auth.currentUser);
    }
  };

  const isGlobalAdmin = claims.role === 'admin_global';
  const effectiveOrgId = isGlobalAdmin
    ? (claims.assumedOrgId ?? null)
    : (claims.orgId ?? null);

  // ── Cross-session propagation of `assumedOrgId` ────────────────────────
  // When another session of the same global admin assumes / releases an org,
  // the backend writes a Firestore signal doc. We watch it and refresh our ID
  // token only when our local claims are out of date — this propagates the new
  // claim to this session without forcing a re-login (no revoke_refresh_tokens).
  useEffect(() => {
    const uid = user?.uid;
    if (!uid || !isGlobalAdmin) return;
    return watchAssumedOrgSignal(uid, sig => {
      const signalOrg = sig?.assumedOrgId ?? null;
      const localOrg  = claimsRef.current.assumedOrgId ?? null;
      if (signalOrg !== localOrg) {
        void refresh();
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, isGlobalAdmin]);

  return (
    <Ctx.Provider value={{ user, profile, claims, loading, effectiveOrgId, isGlobalAdmin, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used within AuthProvider');
  return v;
}
